import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Calendar, TrendingUp, ChevronDown } from "lucide-react";
import { format, parseISO } from "date-fns";

interface CommitDay {
  date: string;
  count: number;
  campaigns: string[];
}

export function CommitSummaryCard() {
  const [recentCommits, setRecentCommits] = useState<CommitDay[]>([]);
  const [totalThisMonth, setTotalThisMonth] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchCommitStats = async () => {
      try {
        // Get mailing history grouped by mailed_date for last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data, error } = await supabase
          .from("mailing_history")
          .select("mailed_date, campaign_name")
          .not("mailed_date", "is", null)
          .gte("mailed_date", thirtyDaysAgo.toISOString().split("T")[0])
          .order("mailed_date", { ascending: false });

        if (error) throw error;

        // Group by date
        const byDate = new Map<string, { count: number; campaigns: Set<string> }>();
        let monthTotal = 0;

        data?.forEach((row) => {
          const d = row.mailed_date!;
          if (!byDate.has(d)) {
            byDate.set(d, { count: 0, campaigns: new Set() });
          }
          const entry = byDate.get(d)!;
          entry.count++;
          if (row.campaign_name) entry.campaigns.add(row.campaign_name);
          monthTotal++;
        });

        const commitDays: CommitDay[] = Array.from(byDate.entries())
          .map(([date, { count, campaigns }]) => ({
            date,
            count,
            campaigns: Array.from(campaigns),
          }))
          .slice(0, 10);

        setRecentCommits(commitDays);
        setTotalThisMonth(monthTotal);
      } catch (err) {
        console.error("Error fetching commit stats:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommitStats();
  }, []);

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Loading...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                Recent Commits to Mail
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </CardTitle>
              <Badge variant="secondary" className="text-xs">
                <TrendingUp className="h-3 w-3 mr-1" />
                {totalThisMonth.toLocaleString()} last 30 days
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {recentCommits.length === 0 ? (
              <p className="text-sm text-muted-foreground">No commits in the last 30 days.</p>
            ) : (
              <div className="space-y-1.5">
                {recentCommits.map((day) => (
                  <div
                    key={day.date}
                    className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">
                        {format(parseISO(day.date), "EEE, MMM d")}
                      </span>
                      {day.campaigns.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          — {day.campaigns.join(", ")}
                        </span>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">
                      {day.count.toLocaleString()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
